// Mercapoli - Executive Dashboard Multi-País + IA
// GO-LIVE: Filtro de país OBLIGATORIO para contexto operativo
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Users, Store, Package, DollarSign, TrendingUp, TrendingDown,
  LogOut, Settings, Bell, ChevronRight, Loader2, RefreshCw,
  ShoppingBag, Bike, BarChart3, Tag, Wallet, Layers, FileCheck,
  Globe, MapPin, Calendar, Sparkles, AlertTriangle, Lightbulb,
  ArrowUpRight, ArrowDownRight, Minus, Flag
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../../components/ui/select';
import { admin as adminApi } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  LineChart, Line, PieChart, Pie, Cell, Legend, AreaChart, Area
} from 'recharts';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_mercadelivery-1/artifacts/5y038jeg_icono_mercapoli.png";

// Colores para gráficos
const COLORS = ['#22c55e', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

// País por defecto (CONFIGURABLE via .env - NO hardcodear ningún país)
const DEFAULT_COUNTRY = process.env.REACT_APP_DEFAULT_COUNTRY || 'GT';

// Configuración de países disponibles (sincronizado con backend)
const PAISES_DISPONIBLES = [
  { codigo: 'GT', nombre: 'Guatemala', emoji: '🇬🇹', moneda: 'GTQ', simbolo: 'Q' },
  { codigo: 'MX', nombre: 'México', emoji: '🇲🇽', moneda: 'MXN', simbolo: '$' },
  { codigo: 'CO', nombre: 'Colombia', emoji: '🇨🇴', moneda: 'COP', simbolo: '$' },
  { codigo: 'PE', nombre: 'Perú', emoji: '🇵🇪', moneda: 'PEN', simbolo: 'S/' },
  { codigo: 'CL', nombre: 'Chile', emoji: '🇨🇱', moneda: 'CLP', simbolo: '$' },
  { codigo: 'AR', nombre: 'Argentina', emoji: '🇦🇷', moneda: 'ARS', simbolo: '$' },
  { codigo: 'US', nombre: 'Estados Unidos', emoji: '🇺🇸', moneda: 'USD', simbolo: '$' },
];

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [stats, setStats] = useState(null);
  const [executiveData, setExecutiveData] = useState(null);
  
  // Filtros - Default: país configurado en .env (NO hardcodeado)
  const [countryFilter, setCountryFilter] = useState(DEFAULT_COUNTRY);
  const [periodFilter, setPeriodFilter] = useState('month');
  
  // País seleccionado info
  const selectedCountry = PAISES_DISPONIBLES.find(p => p.codigo === countryFilter) || 
    PAISES_DISPONIBLES.find(p => p.codigo === DEFAULT_COUNTRY) ||
    { codigo: 'GLOBAL', nombre: 'Global', emoji: '🌍', moneda: 'USD', simbolo: '$' };

  useEffect(() => {
    loadData();
  }, [countryFilter, periodFilter]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Calcular fechas según periodo
      const now = new Date();
      let fromDate = new Date();
      
      switch (periodFilter) {
        case 'today':
          fromDate = new Date(now.setHours(0, 0, 0, 0));
          break;
        case 'week':
          fromDate.setDate(fromDate.getDate() - 7);
          break;
        case 'month':
          fromDate.setMonth(fromDate.getMonth() - 1);
          break;
        case 'year':
          fromDate.setFullYear(fromDate.getFullYear() - 1);
          break;
        default:
          fromDate.setMonth(fromDate.getMonth() - 1);
      }
      
      const params = {
        country_code: countryFilter,
        from_date: fromDate.toISOString().split('T')[0],
        to_date: new Date().toISOString().split('T')[0]
      };
      
      const [dashboardData, execData] = await Promise.all([
        adminApi.getDashboard(),
        adminApi.getExecutiveDashboard(params)
      ]);
      
      setStats(dashboardData);
      setExecutiveData(execData);
    } catch (error) {
      console.error('Error loading dashboard:', error);
    }
    setLoading(false);
    setRefreshing(false);
  };

  const handleRefresh = () => {
    setRefreshing(true);
    loadData();
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const formatCurrency = (value, symbol = '$') => {
    return `${symbol}${value?.toLocaleString('es-GT', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}`;
  };

  const getTrendIcon = (trend) => {
    switch (trend) {
      case 'up': return <ArrowUpRight className="h-4 w-4 text-green-500" />;
      case 'down': return <ArrowDownRight className="h-4 w-4 text-red-500" />;
      default: return <Minus className="h-4 w-4 text-gray-500" />;
    }
  };

  const getInsightIcon = (type) => {
    switch (type) {
      case 'growth': return <TrendingUp className="h-5 w-5 text-green-500" />;
      case 'alert': return <AlertTriangle className="h-5 w-5 text-amber-500" />;
      case 'opportunity': return <Lightbulb className="h-5 w-5 text-blue-500" />;
      case 'trend': return <BarChart3 className="h-5 w-5 text-purple-500" />;
      default: return <Sparkles className="h-5 w-5 text-gray-500" />;
    }
  };

  const getInsightBg = (type) => {
    switch (type) {
      case 'growth': return 'bg-green-50 border-green-200';
      case 'alert': return 'bg-amber-50 border-amber-200';
      case 'opportunity': return 'bg-blue-50 border-blue-200';
      case 'trend': return 'bg-purple-50 border-purple-200';
      default: return 'bg-gray-50 border-gray-200';
    }
  };

  const menuItems = [
    { path: '/admin/usuarios', label: 'Usuarios', icon: Users, color: 'text-blue-500' },
    { path: '/admin/solicitudes', label: 'Solicitudes', icon: FileCheck, color: 'text-yellow-500' },
    { path: '/admin/tipos-comercio', label: 'Tipos de Comercio', icon: Layers, color: 'text-pink-500' },
    { path: '/admin/restaurantes', label: 'Comercios', icon: Store, color: 'text-green-500' },
    { path: '/admin/repartidores', label: 'Repartidores', icon: Bike, color: 'text-purple-500' },
    { path: '/admin/pedidos', label: 'Pedidos', icon: Package, color: 'text-orange-500' },
    { path: '/admin/categorias', label: 'Categorías', icon: Tag, color: 'text-cyan-500' },
    { path: '/admin/finanzas', label: 'Finanzas', icon: Wallet, color: 'text-emerald-500' },
  ];

  const kpis = executiveData?.kpis || {};
  const currency = executiveData?.currency || { symbol: '$', code: 'USD' };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-border/50">
        <div className="container-app">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <img src={LOGO_URL} alt="Mercapoli" className="w-10 h-10" />
              <div>
                <h1 className="font-heading text-lg font-bold">Panel Ejecutivo</h1>
                <p className="text-xs text-muted-foreground">Multi-País • Multi-Moneda</p>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <Button 
                variant="ghost" 
                size="icon"
                onClick={handleRefresh}
                disabled={refreshing}
              >
                <RefreshCw className={`h-5 w-5 ${refreshing ? 'animate-spin' : ''}`} />
              </Button>
              <Button variant="ghost" size="icon">
                <Bell className="h-5 w-5" />
              </Button>
              <Button variant="ghost" size="icon" onClick={handleLogout}>
                <LogOut className="h-5 w-5" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="container-app py-6 space-y-6">
        {/* Banner de País Activo - GO-LIVE Multi-País */}
        <div className="bg-gradient-to-r from-brand-primary/10 to-brand-secondary/10 border border-brand-primary/20 rounded-lg p-4">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-brand-primary/20 flex items-center justify-center text-xl">
                {selectedCountry.emoji}
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Dashboard de</p>
                <p className="font-bold text-lg">{selectedCountry.nombre}</p>
              </div>
              <Badge className="ml-2 bg-brand-primary text-white">
                {selectedCountry.moneda}
              </Badge>
            </div>
            
            <div className="flex items-center gap-4">
              {/* Selector de País */}
              <div className="flex items-center gap-2">
                <Flag className="h-4 w-4 text-brand-primary" />
                <Select value={countryFilter} onValueChange={setCountryFilter}>
                  <SelectTrigger className="w-[180px] border-brand-primary/30">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="GLOBAL" className="font-medium">
                      🌍 Vista Global (USD)
                    </SelectItem>
                    <div className="border-t my-1" />
                    {PAISES_DISPONIBLES.map(pais => (
                      <SelectItem key={pais.codigo} value={pais.codigo}>
                        {pais.emoji} {pais.nombre}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Selector de Periodo */}
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <Select value={periodFilter} onValueChange={setPeriodFilter}>
                  <SelectTrigger className="w-[130px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="today">Hoy</SelectItem>
                    <SelectItem value="week">Semana</SelectItem>
                    <SelectItem value="month">Mes</SelectItem>
                    <SelectItem value="year">Año</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          
          {countryFilter === 'GLOBAL' && (
            <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
              <AlertTriangle className="h-3 w-3" />
              Vista global muestra datos consolidados en USD. Selecciona un país para métricas en moneda local.
            </p>
          )}
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-brand-primary" />
          </div>
        ) : (
          <>
            {/* KPIs Principales */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <DollarSign className="h-8 w-8 text-green-500" />
                    {getTrendIcon(kpis.crecimiento_mom?.trend)}
                  </div>
                  <p className="text-2xl font-bold mt-2">
                    {formatCurrency(kpis.ingresos_brutos, currency.symbol)}
                  </p>
                  <p className="text-sm text-muted-foreground">Ingresos Brutos</p>
                  {kpis.crecimiento_mom && (
                    <p className={`text-xs mt-1 ${kpis.crecimiento_mom.percentage > 0 ? 'text-green-500' : 'text-red-500'}`}>
                      {kpis.crecimiento_mom.percentage > 0 ? '+' : ''}{kpis.crecimiento_mom.percentage}% MoM
                    </p>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <Wallet className="h-8 w-8 text-purple-500" />
                  </div>
                  <p className="text-2xl font-bold mt-2">
                    {formatCurrency(kpis.comisiones_plataforma, currency.symbol)}
                  </p>
                  <p className="text-sm text-muted-foreground">Comisiones</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Margen: {formatCurrency(kpis.margen_neto, currency.symbol)}
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <Package className="h-8 w-8 text-blue-500" />
                  </div>
                  <p className="text-2xl font-bold mt-2">{kpis.pedidos_completados || 0}</p>
                  <p className="text-sm text-muted-foreground">Pedidos Completados</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Total: {kpis.pedidos_totales || 0} | Cancelados: {kpis.pedidos_cancelados || 0}
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <Users className="h-8 w-8 text-amber-500" />
                  </div>
                  <p className="text-2xl font-bold mt-2">{kpis.usuarios_activos || 0}</p>
                  <p className="text-sm text-muted-foreground">Usuarios Activos</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Ticket promedio: {formatCurrency(kpis.ticket_promedio, currency.symbol)}
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Segunda fila de KPIs */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-950/20 dark:to-emerald-950/20">
                <CardContent className="pt-6">
                  <p className="text-sm font-medium text-green-700 dark:text-green-400">Tasa de Conversión</p>
                  <p className="text-3xl font-bold text-green-600">{kpis.tasa_conversion || 0}%</p>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-950/20 dark:to-indigo-950/20">
                <CardContent className="pt-6">
                  <p className="text-sm font-medium text-blue-700 dark:text-blue-400">Crecimiento MoM</p>
                  <p className={`text-3xl font-bold ${(kpis.crecimiento_mom?.percentage || 0) >= 0 ? 'text-blue-600' : 'text-red-600'}`}>
                    {(kpis.crecimiento_mom?.percentage || 0) > 0 ? '+' : ''}{kpis.crecimiento_mom?.percentage || 0}%
                  </p>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-purple-50 to-violet-50 dark:from-purple-950/20 dark:to-violet-950/20">
                <CardContent className="pt-6">
                  <p className="text-sm font-medium text-purple-700 dark:text-purple-400">YoY</p>
                  <p className={`text-3xl font-bold ${(executiveData?.yoy_comparison?.growth_percentage || 0) >= 0 ? 'text-purple-600' : 'text-red-600'}`}>
                    {(executiveData?.yoy_comparison?.growth_percentage || 0) > 0 ? '+' : ''}{executiveData?.yoy_comparison?.growth_percentage || 0}%
                  </p>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-950/20 dark:to-orange-950/20">
                <CardContent className="pt-6">
                  <p className="text-sm font-medium text-amber-700 dark:text-amber-400">Costos Envío</p>
                  <p className="text-3xl font-bold text-amber-600">
                    {formatCurrency(kpis.costos_envio, currency.symbol)}
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* AI Insights - Contextualizados por País */}
            {executiveData?.ai_insights?.insights?.length > 0 && (
              <Card>
                <CardHeader>
                  <div className="flex items-center gap-2">
                    <Sparkles className="h-5 w-5 text-brand-primary" />
                    <CardTitle>Insights Ejecutivos</CardTitle>
                    <Badge variant="secondary" className="text-xs">IA</Badge>
                    <Badge className="text-xs bg-brand-primary/10 text-brand-primary border-brand-primary/30">
                      {selectedCountry.emoji} {selectedCountry.nombre}
                    </Badge>
                  </div>
                  <CardDescription>
                    Análisis automático de métricas {countryFilter === 'GLOBAL' ? 'globales' : `para ${selectedCountry.nombre}`}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid gap-3 md:grid-cols-2">
                    {executiveData.ai_insights.insights.map((insight, idx) => (
                      <motion.div
                        key={idx}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: idx * 0.1 }}
                        className={`p-4 rounded-lg border ${getInsightBg(insight.type)}`}
                      >
                        <div className="flex items-start gap-3">
                          {getInsightIcon(insight.type)}
                          <div className="flex-1">
                            <p className="font-semibold text-sm">{insight.title}</p>
                            <p className="text-sm text-muted-foreground mt-1">
                              {insight.description}
                            </p>
                            <Badge 
                              variant={insight.priority === 'high' ? 'destructive' : 'outline'} 
                              className="mt-2 text-xs"
                            >
                              {insight.priority === 'high' ? 'Alta' : insight.priority === 'medium' ? 'Media' : 'Baja'}
                            </Badge>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Gráficos y Tablas */}
            <div className="grid gap-6 lg:grid-cols-2">
              {/* Por País */}
              {executiveData?.by_country?.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Globe className="h-5 w-5" />
                      Ingresos por País
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={executiveData.by_country}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="country_code" />
                          <YAxis />
                          <Tooltip 
                            formatter={(value) => [`$${value.toLocaleString()}`, 'Ingresos USD']}
                          />
                          <Bar dataKey="revenue_usd" fill="#22c55e" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="mt-4 space-y-2">
                      {executiveData.by_country.slice(0, 5).map((country, idx) => (
                        <div key={country.country_code} className="flex items-center justify-between text-sm">
                          <span className="flex items-center gap-2">
                            <span className="font-medium">{country.country_name}</span>
                            <Badge variant="outline" className="text-xs">{country.orders} pedidos</Badge>
                          </span>
                          <span className="font-bold">${country.revenue_usd.toLocaleString()}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Por Ciudad */}
              {executiveData?.by_city?.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <MapPin className="h-5 w-5" />
                      Top Ciudades
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={executiveData.by_city.slice(0, 6)}
                            dataKey="revenue_usd"
                            nameKey="city_name"
                            cx="50%"
                            cy="50%"
                            outerRadius={80}
                            label={({ city_name, percent }) => `${city_name} ${(percent * 100).toFixed(0)}%`}
                          >
                            {executiveData.by_city.slice(0, 6).map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip formatter={(value) => [`$${value.toLocaleString()}`, 'Ingresos']} />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="mt-4 space-y-2">
                      {executiveData.by_city.slice(0, 5).map((city, idx) => (
                        <div key={city.city_id} className="flex items-center justify-between text-sm">
                          <span className="flex items-center gap-2">
                            <span 
                              className="w-3 h-3 rounded-full" 
                              style={{ backgroundColor: COLORS[idx % COLORS.length] }}
                            />
                            <span>{city.city_name}</span>
                          </span>
                          <span className="font-medium">
                            Ticket: {formatCurrency(city.avg_ticket, currency.symbol)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Tendencias */}
            {executiveData?.trends?.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="h-5 w-5" />
                    Tendencia de Ingresos
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={executiveData.trends}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" tickFormatter={(d) => new Date(d).toLocaleDateString('es', { day: '2-digit', month: 'short' })} />
                        <YAxis />
                        <Tooltip 
                          labelFormatter={(d) => new Date(d).toLocaleDateString('es', { weekday: 'long', day: 'numeric', month: 'long' })}
                          formatter={(value, name) => [
                            name === 'revenue' ? formatCurrency(value, currency.symbol) : value,
                            name === 'revenue' ? 'Ingresos' : 'Pedidos'
                          ]}
                        />
                        <Area 
                          type="monotone" 
                          dataKey="revenue" 
                          stroke="#22c55e" 
                          fill="#22c55e" 
                          fillOpacity={0.2}
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Menú de navegación */}
            <Card>
              <CardHeader>
                <CardTitle>Gestión</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {menuItems.map((item) => (
                    <Link
                      key={item.path}
                      to={item.path}
                      className="flex items-center gap-3 p-4 rounded-lg border hover:bg-muted transition-colors"
                    >
                      <item.icon className={`h-5 w-5 ${item.color}`} />
                      <span className="font-medium text-sm">{item.label}</span>
                    </Link>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Comparativa YoY */}
            {executiveData?.yoy_comparison && (
              <Card className="bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-950/20 dark:to-purple-950/20">
                <CardContent className="pt-6">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Comparativa Interanual (YoY)</p>
                      <p className="text-3xl font-bold mt-1">
                        {executiveData.yoy_comparison.growth_percentage > 0 ? '+' : ''}
                        {executiveData.yoy_comparison.growth_percentage}%
                      </p>
                    </div>
                    <div className="flex gap-8">
                      <div className="text-center">
                        <p className="text-xs text-muted-foreground">Periodo Actual</p>
                        <p className="text-lg font-bold">{executiveData.yoy_comparison.current_period.orders} pedidos</p>
                        <p className="text-sm">{formatCurrency(executiveData.yoy_comparison.current_period.revenue, currency.symbol)}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-xs text-muted-foreground">Año Anterior</p>
                        <p className="text-lg font-bold">{executiveData.yoy_comparison.previous_period.orders} pedidos</p>
                        <p className="text-sm">{formatCurrency(executiveData.yoy_comparison.previous_period.revenue, currency.symbol)}</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
